import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export function CalendarTab() {
  const { members, activeMember } = useAuth();
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", time: "", notes: "", member_id: "" });

  const load = async () => setEvents((await api.get("/events")).data);
  useEffect(() => { load(); }, []);

  const y = cursor.getFullYear();
  const mo = cursor.getMonth();
  const first = new Date(y, mo, 1).getDay();
  const daysIn = new Date(y, mo + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: daysIn }, (_, i) => i + 1)];
  const iso = (d) => `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayIso = new Date().toISOString().slice(0, 10);

  const memberColor = (id) => members.find((m) => m.id === id)?.color || "#64748B";

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) return toast.error("Add a title and date");
    await api.post("/events", { ...form, member_id: form.member_id || activeMember?.id });
    setForm({ title: "", date: "", time: "", notes: "", member_id: "" });
    setShowForm(false);
    load();
    toast.success("Event added to the family calendar 📅");
  };
  const del = async (id) => { await api.delete(`/events/${id}`); load(); };

  const monthEvents = events
    .filter((e) => e.date.startsWith(`${y}-${String(mo + 1).padStart(2, "0")}`))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-extrabold" style={{ fontFamily: "Outfit" }}>Family Calendar</h2>
          <p className="text-slate-500 font-medium">See everyone's plans before making your own.</p>
        </div>
        <button data-testid="add-event-btn" onClick={() => setShowForm((s) => !s)}
          className="btn-tactile bg-[#3B82F6] text-white px-5 py-2.5">+ Add appointment</button>
      </div>

      {showForm && (
        <form onSubmit={add} className="card-tactile p-5 grid sm:grid-cols-2 gap-3">
          <input data-testid="event-title-input" placeholder="What's happening?" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 outline-none font-medium sm:col-span-2" />
          <input data-testid="event-date-input" type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 outline-none font-medium" />
          <input type="time" value={form.time}
            onChange={(e) => setForm({ ...form, time: e.target.value })}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 outline-none font-medium" />
          <select value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 outline-none font-medium">
            <option value="">Whole family</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <input placeholder="Notes (optional)" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 outline-none font-medium" />
          <button data-testid="save-event-btn" type="submit" className="btn-tactile bg-[#10B981] text-white py-2.5 sm:col-span-2">Save event</button>
        </form>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card-tactile p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCursor(new Date(y, mo - 1, 1))} className="btn-tactile bg-white px-3 py-1">‹</button>
            <div className="font-extrabold text-lg" style={{ fontFamily: "Outfit" }}>{MONTHS[mo]} {y}</div>
            <button onClick={() => setCursor(new Date(y, mo + 1, 1))} className="btn-tactile bg-white px-3 py-1">›</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-slate-400 mb-1">
            {DOW.map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const dayEvents = events.filter((e) => e.date === iso(d));
              const isToday = iso(d) === todayIso;
              return (
                <div key={i} className={`min-h-[64px] rounded-lg border-2 p-1 text-left ${isToday ? "border-[#FF5A8B] bg-[#FFF0F5]" : "border-slate-100"}`}>
                  <div className="text-xs font-bold text-slate-500">{d}</div>
                  <div className="space-y-0.5 mt-0.5">
                    {dayEvents.slice(0, 3).map((e) => (
                      <div key={e.id} className="text-[9px] font-bold text-white rounded px-1 truncate"
                        style={{ background: memberColor(e.member_id) }} title={e.title}>{e.title}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-tactile p-5">
          <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Upcoming this month</h3>
          <div className="space-y-2 max-h-[420px] overflow-auto">
            {monthEvents.length === 0 && <p className="text-slate-400 font-medium text-sm">No plans yet.</p>}
            {monthEvents.map((e) => (
              <div key={e.id} data-testid="event-item" className="flex items-start gap-2 rounded-xl border-2 border-slate-100 p-2.5">
                <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: memberColor(e.member_id) }} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{e.title}</div>
                  <div className="text-xs text-slate-500 font-medium">
                    {e.date}{e.time && ` · ${e.time}`}{e.member_id && ` · ${members.find((m) => m.id === e.member_id)?.name || ""}`}
                  </div>
                  {e.notes && <div className="text-xs text-slate-400">{e.notes}</div>}
                </div>
                <button onClick={() => del(e.id)} className="text-slate-300 hover:text-red-500 font-bold">×</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
